import React, { useEffect, useMemo, useRef, useState } from "react";
import { easeCubicOut } from "d3-ease";
import { map, max, min, reject, size, sortBy, take } from "lodash";
import { animated, useTrail } from "react-spring";
import styled from "styled-components";
import { Color, ZIndexes, content, useTheme } from "@glossy";
import { useIsMounted, useSize } from "@lkf/hooks";
import { normalize } from "@tootools/number";
import { natural } from "@tootools/sort";
import Icon from "@ui-kit/icon";

export interface CloudItem {
  label: string;
  weight: number;
  color?: string;
  id: string | number;
  isHidden?: boolean;
}

const Root = styled(animated.div)`
  text-align: center;
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  align-content: center;
  overflow: hidden;
`;

const ItemWrapper = styled.span`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Eyecon = styled.div<{ isHidden?: boolean }>`
  position: absolute;
  color: ${Color.White};
  border-radius: 50%;
  font-size: 0;
  padding: 6px;
  z-index: ${ZIndexes.AboveContent};
  pointer-events: none;
  background-color: ${({ isHidden }) => !isHidden && Color.SlateGrey};
`;

export interface IProps {
  items: CloudItem[];
  minMaxRatio?: number;
  renderItem: (
    item: CloudItem & { normalizedWeight?: number },
    animated?: boolean
  ) => React.ReactNode;
  color?: string;
  order?: "label" | "weight";
  limit?: number;
  className?: string;
  disableAnimation?: boolean;
  isHideable?: boolean;
}

type IItem = {
  item: CloudItem;
  renderItem: IProps["renderItem"];
  opacity: any;
};

const MAX_FONT_SIZE = 100;

const Item = ({ item, opacity, renderItem }: IItem) => {
  return <span style={{ opacity }}>{renderItem(item, opacity !== 1)}</span>;
};

const HideableItem = ({ item, opacity, renderItem }: IItem) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <ItemWrapper
      style={{ opacity }}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      {item.isHidden && !isHovered && (
        <Eyecon isHidden>
          <Icon glyph="eye-crossed" size="m" />
        </Eyecon>
      )}
      {isHovered && (
        <Eyecon>
          <Icon glyph={item.isHidden ? "eye" : "eye-crossed"} size="m" />
        </Eyecon>
      )}
      {renderItem(item, opacity !== 1)}
    </ItemWrapper>
  );
};

const AnimatedItem = animated(Item);
const AnimatedHideableItem = animated(HideableItem);

const ChartyCloud = ({
  items,
  minMaxRatio = 0.5, // Ratio between minimum and maximum weight
  renderItem,
  order,
  limit,
  className,
  disableAnimation = false,
  isHideable = false,
}: IProps) => {
  const theme = useTheme();
  const defaultColor = content({ theme }, "secondary");
  const root = useRef(null);
  const { width, height } = useSize(root);
  const isMounted = useIsMounted();
  const [fontSize, setFontSize] = useState(0);
  const [visible, setVisible] = useState(false);

  // Normalize item weights based on `minMaxRatio`
  const normalizedItems = useMemo(() => {
    const filteredItems = sortBy(
      reject(items, (item) => !item.weight),
      (item) => -item.weight
    );

    // Slice items based on `limit`
    const slicedItems = limit ? take(filteredItems, limit) : filteredItems;

    // Sort items based on `order`
    const sortedItems =
      order === "label"
        ? natural(slicedItems, "label")
        : order === "weight"
        ? sortBy(slicedItems, (item) => -item.weight)
        : slicedItems;

    const weights = map(sortedItems, "weight");
    const minWeight = min(weights);
    const maxWeight = max(weights);
    return map(sortedItems, (item) => ({
      ...item,
      color: item.color || defaultColor,
      normalizedWeight: normalize(
        [minMaxRatio, 1],
        minWeight,
        maxWeight,
        item.weight
      ),
    }));
  }, [defaultColor, limit, items, minMaxRatio, order]);

  const [trail, set] = useTrail(normalizedItems.length, () => ({
    config: { duration: disableAnimation ? 0 : 1000, easing: easeCubicOut },
    from: { opacity: 0 },
  }));

  // Launch resize
  useEffect(() => {
    if (isMounted() && width && height) {
      if (size(normalizedItems)) {
        setFontSize(MAX_FONT_SIZE);
      }
      //don't really know why we need this, but it breaks in cloud editing when there is only one item (nothing is displayed)
      // set({ opacity: 0 });
      // setVisible(false);
    } else {
      set({ opacity: 1 });
      setVisible(true);
    }
  }, [
    isMounted,
    width,
    height,
    normalizedItems,
    minMaxRatio,
    order,
    limit,
    set,
  ]);

  // After each fontSize update
  useEffect(() => {
    if (fontSize !== 0) {
      if (root.current.scrollHeight > root.current.clientHeight) {
        // Vertical resize
        setFontSize(
          fontSize /
            Math.min(root.current.scrollHeight / root.current.clientHeight, 2)
        );
      } else if (root.current.scrollWidth > root.current.clientWidth) {
        // Horizontal resize
        setFontSize(
          fontSize /
            Math.min(root.current.scrollWidth / root.current.clientWidth, 2)
        );
      } else {
        set({ opacity: 1 });
        setVisible(true);
      }
    }
  }, [fontSize, set]);

  return (
    <Root
      ref={root}
      className={className}
      style={{ fontSize, visibility: visible ? "visible" : "hidden" }}
    >
      {map(trail, (animation, idx) => {
        const item = normalizedItems[idx];
        return isHideable ? (
          <AnimatedHideableItem
            renderItem={renderItem}
            item={item}
            opacity={animation.opacity}
            key={item ? item.id : idx}
          />
        ) : (
          <AnimatedItem
            renderItem={renderItem}
            item={item}
            opacity={animation.opacity}
            key={item ? item.id : idx}
          />
        );
      })}
    </Root>
  );
};

export default ChartyCloud;
